import { useState } from "react";
import { useT } from "@/i18n";
import { ExternalLink, Search, BookOpen, NotebookText, Mail, FolderOpen, Plus } from "lucide-react";

// External Links panel. Only references/URIs are stored — never content.
// All five integrations are always visible; unconfigured ones render inactive with an "Add link" action.
export default function ExternalLinks({ matter, settings, onAdd }) {
  const { t } = useT();
  const [everythingHint, setEverythingHint] = useState(false);

  const FIELD_MAP = {
    client: matter.client,
    name: matter.name,
    internal_ref: matter.internal_ref,
    court_case_number: matter.court_case_number,
  };
  const fields = settings?.everything_fields?.length
    ? settings.everything_fields
    : ["client", "name", "internal_ref", "court_case_number"];
  const defaultQuery = fields.map((f) => FIELD_MAP[f]).filter(Boolean).join(" ");
  const everythingQuery = (matter.everything_query || "").trim() || defaultQuery;

  const obsidianUri = (() => {
    const note = (matter.obsidian_note || "").trim();
    const vault = (matter.obsidian_vault || settings?.obsidian_default_vault || "").trim();
    if (note.startsWith("obsidian://")) return note;
    if (note || vault) {
      return `obsidian://open?vault=${encodeURIComponent(vault)}${note ? `&file=${encodeURIComponent(note)}` : ""}`;
    }
    return null;
  })();

  const norm = (u) => (/^[a-z][a-z0-9+.-]*:/i.test(u) ? u : `https://${u}`);
  const dropboxHref = (matter.dropbox_link || "").trim() ? norm(matter.dropbox_link.trim()) : null;
  const outlookHref = (matter.outlook_ref || "").trim() ? norm(matter.outlook_ref.trim()) : null;
  const zoteroHref = (matter.zotero_uri || "").trim() || null;

  const items = [
    { key: "dropbox", icon: FolderOpen, label: t("ext.dropbox"), href: dropboxHref },
    {
      key: "everything", icon: Search, label: t("ext.everything"),
      onClick: () => {
        window.location.href = `es:${everythingQuery}`;
        setEverythingHint(true);
      },
    },
    { key: "zotero", icon: BookOpen, label: t("ext.zotero"), href: zoteroHref },
    { key: "obsidian", icon: NotebookText, label: t("ext.obsidian"), href: obsidianUri },
    { key: "outlook", icon: Mail, label: t("ext.outlook"), href: outlookHref },
  ];

  const activeCls =
    "inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-200 rounded-md px-3 py-2 text-slate-700 hover:border-slate-800 hover:text-slate-900 transition-colors";

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="external-links-panel">
      <h3 className="font-serif text-lg text-slate-800 mb-4">{t("ext.title")}</h3>
      <div className="flex flex-wrap gap-2">
        {items.map((b) => {
          const Icon = b.icon;
          const configured = Boolean(b.href || b.onClick);
          if (!configured) {
            return (
              <span key={b.key} className="inline-flex items-center gap-1.5"
                data-testid={`ext-${b.key}-button`} data-configured="false">
                <span
                  title={t("ext.not_configured")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-dashed border-slate-300 rounded-md px-3 py-2 text-slate-400"
                >
                  <Icon className="h-3.5 w-3.5" /> {b.label}
                </span>
                {onAdd && (
                  <button
                    type="button"
                    onClick={onAdd}
                    data-testid={`ext-${b.key}-add`}
                    className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-700 hover:text-amber-900 transition-colors"
                  >
                    <Plus className="h-3 w-3" /> {t("ext.add_link")}
                  </button>
                )}
              </span>
            );
          }
          return b.href ? (
            <a key={b.key} href={b.href} data-testid={`ext-${b.key}-button`} data-configured="true" className={activeCls}>
              <Icon className="h-3.5 w-3.5" /> {b.label} <ExternalLink className="h-3 w-3 text-slate-400" />
            </a>
          ) : (
            <button key={b.key} type="button" onClick={b.onClick} data-testid={`ext-${b.key}-button`} data-configured="true" className={activeCls}>
              <Icon className="h-3.5 w-3.5" /> {b.label}
            </button>
          );
        })}
      </div>
      {everythingHint && (
        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed" data-testid="everything-hint">
          {t("ext.everything_hint")}
        </p>
      )}
      <p className="text-[11px] text-slate-400 mt-3">{t("ext.edit_hint")}</p>
    </div>
  );
}
